import { Modal, ModalBody, ModalContent, ModalHeader, Spinner } from '@nextui-org/react'
import { CSSProperties, FC, useCallback, useMemo, useState } from 'react'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import { DAY_NAMES, FOUNDATION, TONES, COURIER } from '../constants'
import { useLogisticBox } from '../context/LogisticBoxContext'
import { useApi } from '../hooks/useApi'
import { useSelectedSeller } from '../hooks/useSelectedSeller'
import { calendarApi, CalendarDayActivity } from '../api'
import { fmtDay, isoWeek, startOfWeek } from '../utils'

const toIsoDate = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const dayStyle = (tone: CalendarDayActivity['tone']): CSSProperties => {
  if (tone === 'gray') {
    return { background: '#fff', borderColor: '#e5e7eb', color: 'rgba(21,25,48,0.6)' }
  }
  const t = TONES[tone]
  return { background: t.bg, borderColor: `${t.ink}4d`, color: t.ink }
}

const tagFor = (d: CalendarDayActivity): string => {
  if (d.delivered > 0) return 'Entregas'
  if (d.picked_ups > 0) return `Retiros ${COURIER}`
  if (d.collections > 0) return 'Recolecciones'
  return 'Sin actividad'
}

const noteFor = (d: CalendarDayActivity): string => {
  const parts: string[] = []
  if (d.collections > 0) parts.push(`${d.collections} recolección(es)`)
  if (d.picked_ups > 0) parts.push(`${d.picked_ups} retiro(s)`)
  if (d.delivered > 0) parts.push(`${d.delivered} entregada(s)`)
  return parts.join(' · ') || 'Sin movimiento'
}

const CalendarModal: FC = () => {
  const { calendarOpen, closeCalendar, setActiveView } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const [weekOffset, setWeekOffset] = useState(0)

  const today = useMemo(() => new Date(), [])
  const base = useMemo(() => startOfWeek(today), [today])
  const weekStart = useMemo(() => {
    const d = new Date(base)
    d.setDate(base.getDate() + weekOffset * 7)
    return d
  }, [base, weekOffset])
  const weekEnd = useMemo(() => {
    const d = new Date(weekStart)
    d.setDate(weekStart.getDate() + 6)
    return d
  }, [weekStart])

  const fromIso = useMemo(() => toIsoDate(weekStart), [weekStart])
  const toIso = useMemo(() => toIsoDate(weekEnd), [weekEnd])

  const fetcher = useCallback(async () => {
    if (!calendarOpen) return [] as CalendarDayActivity[]
    return await calendarApi.activity(idSeller, fromIso, toIso)
  }, [calendarOpen, fromIso, toIso, idSeller])

  const data = useApi(fetcher, [calendarOpen, fromIso, toIso, idSeller])

  const byDate = useMemo(() => {
    const m = new Map<string, CalendarDayActivity>()
    for (const d of data.data ?? []) m.set(d.fecha, d)
    return m
  }, [data.data])

  return (
    <Modal isOpen={calendarOpen} onClose={closeCalendar} size='3xl' backdrop='blur'>
      <ModalContent>
        <ModalHeader className='flex items-start justify-between'>
          <div>
            <div className='text-[10px] uppercase tracking-widest font-bold' style={{ color: 'rgba(55,65,127,0.6)' }}>
              Calendario · actividad por día
            </div>
            <h2 className='text-lg font-bold' style={{ color: FOUNDATION }}>
              Semana {isoWeek(weekStart)} · {fmtDay(weekStart)} – {fmtDay(weekEnd)} {weekEnd.getFullYear()}
            </h2>
            <p className='text-xs text-[#151930]/60 mt-0.5'>
              Las recolecciones se gatillan por evento o cron, no por día fijo. Aquí ves el resumen real.
            </p>
          </div>
          <div className='flex items-center gap-1'>
            <button onClick={() => setWeekOffset(o => o - 1)} className='w-7 h-7 rounded-lg border border-gray-200 hover:bg-[#f3f4fb] flex items-center justify-center'>
              <MdChevronLeft />
            </button>
            <button onClick={() => setWeekOffset(0)} className='px-2 h-7 text-[14px] text-[#4b75f7] font-medium rounded-lg border border-gray-200 hover:bg-[#f3f4fb]'>
              Hoy
            </button>
            <button onClick={() => setWeekOffset(o => o + 1)} className='w-7 h-7 rounded-lg border border-gray-200 hover:bg-[#f3f4fb] flex items-center justify-center'>
              <MdChevronRight />
            </button>
          </div>
        </ModalHeader>

        <ModalBody>
          {data.loading && (
            <div className='py-8 flex items-center justify-center'>
              <Spinner color='primary' />
            </div>
          )}

          {data.error !== null && data.error !== '' && (
            <div className='text-xs text-rose-500 py-2'>{data.error}</div>
          )}

          {!data.loading && data.error === null && (
            <div className='grid grid-cols-7 gap-2'>
              {Array.from({ length: 7 }).map((_, i) => {
                const d = new Date(weekStart)
                d.setDate(weekStart.getDate() + i)
                const iso = toIsoDate(d)
                const dayIdx = d.getDay()
                const activity = byDate.get(iso) ?? {
                  fecha: iso, collections: 0, picked_ups: 0, delivered: 0, tone: 'gray' as const
                }
                const isToday = d.toDateString() === today.toDateString() && weekOffset === 0
                return (
                  <button
                    key={i}
                    onClick={() => { closeCalendar(); setActiveView('dashboard') }}
                    className={`relative flex flex-col border rounded-xl p-3 text-left transition hover:-translate-y-0.5 ${isToday ? 'ring-2 ring-[#4b75f7]' : ''}`}
                    style={dayStyle(activity.tone)}
                  >
                    <div className='flex items-baseline justify-between mb-2'>
                      <span className='text-[10px] uppercase font-bold opacity-70'>{DAY_NAMES[dayIdx]}</span>
                      <span className='text-2xl font-bold leading-none'>{d.getDate()}</span>
                    </div>
                    <div className='border-t border-current/20 pt-2'>
                      <div className='text-[10px] uppercase font-bold'>{tagFor(activity)}</div>
                      <div className='text-[10px] opacity-70 mt-0.5'>{noteFor(activity)}</div>
                    </div>
                    {isToday && (
                      <span className='absolute top-2 right-2 text-[9px] text-white px-1.5 py-0.5 rounded' style={{ background: FOUNDATION }}>
                        HOY
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          <div className='mt-4 flex flex-wrap items-center gap-4 text-[14px] text-[#151930]/60 pt-3 border-t border-gray-100'>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: TONES.amber.ink }} />Recolección gatillada</span>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: TONES.blue.ink }} />Retiro {COURIER}</span>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: TONES.green.ink }} />Entrega en bodega</span>
            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full bg-gray-300' />Sin actividad</span>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default CalendarModal
